export interface WizardStep {
  id: number;
  title: string;
  icon: string;
}

export interface WizardData {
  // Basic Info
  name: string;
  purpose: string;
  
  // Voice Selection
  voiceId: string;
  ttsModel: string;
  
  // Language & Personality
  primaryLanguage: string;
  additionalLanguages: string[];
  systemPrompt: string;
  
  // AI Model
  llmProvider: 'google' | 'openai' | 'anthropic';
  llmModel: string;
  temperature: number;
  
  // Fine-tuning
  firstMessage: string;
  turnTimeout: number;
  maxDuration: number;
}

export interface Voice {
  voice_id: string;
  name: string;
  category: string;
  preview_url?: string;
  labels?: { [key: string]: string };
  description?: string;
  accent?: string;
  age?: string;
  gender?: string;
  use_case?: string;
  sharing?: {
    status: string;
    history_item_sample_id?: string;
    original_voice_id?: string;
    public_owner_id?: string;
    whitelisted_emails?: string[];
  };
}

export const AGENT_PURPOSES = [
  { value: 'customer_support', label: 'Customer Support', icon: '🎧' },
  { value: 'outbound_sales', label: 'Outbound Sales', icon: '📈' },
  { value: 'learning_development', label: 'Learning and Development', icon: '📚' },
  { value: 'scheduling', label: 'Scheduling', icon: '📅' },
  { value: 'lead_qualification', label: 'Lead Qualification', icon: '👥' },
  { value: 'answering_service', label: 'Answering Service', icon: '📞' },
  { value: 'property_search', label: 'Property Search', icon: '🏠' },
  { value: 'viewing_appointments', label: 'Viewing Appointments', icon: '📋' },
  { value: 'market_information', label: 'Market Information', icon: '📊' },
  { value: 'mortgage_guidance', label: 'Mortgage Guidance', icon: '💳' },
  { value: 'listing_information', label: 'Listing Information', icon: '📄' },
  { value: 'other', label: 'Other', icon: '❓' },
];

export const TTS_MODELS = [
  {
    id: 'eleven_turbo_v2',
    name: 'Turbo v2',
    description: 'High quality, low latency (~250ms)',
    languages: 32,
    icon: '🚀',
    recommended: ['real-time', 'conversation']
  },
  {
    id: 'eleven_multilingual_v2',
    name: 'Multilingual v2',
    description: 'Best quality, emotional expression',
    languages: 29,
    icon: '🌍',
    recommended: ['quality', 'emotion']
  },
  {
    id: 'eleven_monolingual_v1',
    name: 'Monolingual v1',
    description: 'English only, standard quality',
    languages: 1,
    icon: '🎯',
    recommended: ['english-only']
  }
];

export const LLM_MODELS = {
  google: [
    { id: 'gemini-2.0-flash-001', name: 'Gemini 2.0 Flash', description: 'Fast and efficient', icon: '⚡' },
    { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', description: 'Latest model', icon: '✨' },
  ],
  openai: [
    { id: 'gpt-4', name: 'GPT-4', description: 'Most capable', icon: '🚀' },
    { id: 'gpt-4-turbo', name: 'GPT-4 Turbo', description: 'Faster GPT-4', icon: '💨' },
    { id: 'gpt-4o', name: 'GPT-4 Optimized', description: 'Optimized version', icon: '⚡' },
    { id: 'gpt-4o-mini', name: 'GPT-4 Mini', description: 'Cost-effective', icon: '💰' },
    { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo', description: 'Fast and affordable', icon: '🏃' },
  ],
  anthropic: [
    { id: 'claude-3.7-sonnet', name: 'Claude 3.7 Sonnet', description: 'Advanced reasoning', icon: '🎵' },
    { id: 'claude-sonnet-4', name: 'Claude Sonnet 4', description: 'Latest Claude', icon: '🎼' },
  ]
};

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
  { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
  { code: 'pl', name: 'Polish', flag: '🇵🇱' },
  { code: 'ru', name: 'Russian', flag: '🇷🇺' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷' },
  { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
  { code: 'id', name: 'Indonesian', flag: '🇮🇩' },
  { code: 'tr', name: 'Turkish', flag: '🇹🇷' },
  { code: 'sv', name: 'Swedish', flag: '🇸🇪' },
  { code: 'da', name: 'Danish', flag: '🇩🇰' },
  { code: 'fi', name: 'Finnish', flag: '🇫🇮' },
  { code: 'no', name: 'Norwegian', flag: '🇳🇴' },
  { code: 'cs', name: 'Czech', flag: '🇨🇿' },
  { code: 'el', name: 'Greek', flag: '🇬🇷' },
  { code: 'ro', name: 'Romanian', flag: '🇷🇴' },
  { code: 'bg', name: 'Bulgarian', flag: '🇧🇬' },
  { code: 'uk', name: 'Ukrainian', flag: '🇺🇦' },
  { code: 'hr', name: 'Croatian', flag: '🇭🇷' },
  { code: 'sk', name: 'Slovak', flag: '🇸🇰' },
  { code: 'hu', name: 'Hungarian', flag: '🇭🇺' },
  { code: 'vi', name: 'Vietnamese', flag: '🇻🇳' },
  { code: 'ta', name: 'Tamil', flag: '🇮🇳' },
  { code: 'ms', name: 'Malay', flag: '🇲🇾' },
  { code: 'fil', name: 'Filipino', flag: '🇵🇭' },
];

// Sample voices with comprehensive tag information
export const SAMPLE_VOICES: Voice[] = [
  {
    voice_id: 'cjVigY5qzO86Huf0OWal',
    name: 'Rachel',
    category: 'Professional',
    description: 'Warm, professional female voice',
    accent: 'american',
    age: 'young_adult',
    gender: 'female',
    use_case: 'customer_service',
    labels: {
      accent: 'american',
      description: 'calm',
      age: 'young_adult',
      gender: 'female',
      use_case: 'customer_service'
    }
  },
  {
    voice_id: 'JBFqnCBsd6RMkjVDRZzb',
    name: 'George',
    category: 'Authoritative', 
    description: 'Clear, authoritative male voice',
    accent: 'british',
    age: 'middle_aged',
    gender: 'male',
    use_case: 'professional',
    labels: {
      accent: 'british',
      description: 'authoritative',
      age: 'middle_aged',
      gender: 'male',
      use_case: 'professional'
    }
  },
  {
    voice_id: '21m00Tcm4TlvDq8ikWAM',
    name: 'Rachel',
    category: 'Friendly',
    description: 'Friendly, conversational female voice',
    accent: 'american',
    age: 'young_adult',
    gender: 'female',
    use_case: 'conversational',
    labels: {
      accent: 'american',
      description: 'friendly',
      age: 'young_adult',
      gender: 'female',
      use_case: 'conversational'
    }
  },
  {
    voice_id: 'pMsXgVXv3BLzUgSXRplE',
    name: 'Clyde',
    category: 'Casual',
    description: 'Casual, approachable male voice',
    accent: 'american',
    age: 'middle_aged',
    gender: 'male',
    use_case: 'casual',
    labels: {
      accent: 'american',
      description: 'casual',
      age: 'middle_aged',
      gender: 'male',
      use_case: 'casual'
    }
  }
];

export const PROMPT_TEMPLATES: Record<string, string> = {
  customer_service: `You are a helpful and professional customer service representative. Your goals are to:
- Greet customers warmly and professionally
- Listen carefully to their concerns
- Provide accurate and helpful information
- Resolve issues efficiently
- Maintain a positive and patient demeanor
- Follow company policies while showing empathy

Always prioritize customer satisfaction while being honest and transparent.`,

  sales: `You are a knowledgeable and consultative sales agent. Your approach should:
- Build rapport with potential customers
- Ask discovery questions to understand their needs
- Present relevant solutions and benefits
- Handle objections professionally
- Guide customers through the decision-making process
- Be persuasive but never pushy

Focus on creating value and solving customer problems.`,

  tech_support: `You are a skilled technical support specialist. Your responsibilities include:
- Diagnosing technical issues systematically
- Explaining solutions in clear, simple terms
- Walking users through troubleshooting steps
- Escalating complex issues when necessary
- Documenting problems and solutions
- Being patient with users of all technical levels

Prioritize getting users back up and running quickly.`,

  appointment: `You are an efficient appointment scheduling assistant. Your tasks are to:
- Check calendar availability
- Suggest suitable time slots
- Confirm appointment details
- Send reminders and confirmations
- Handle rescheduling requests
- Maintain accurate records

Be organized, clear, and accommodating to scheduling preferences.`,

  personal_assistant: `You are a capable and proactive personal assistant. Your role involves:
- Managing tasks and reminders
- Providing helpful information
- Organizing schedules and priorities
- Offering suggestions and recommendations
- Maintaining confidentiality
- Anticipating needs

Be efficient, trustworthy, and adaptable to various requests.`,

  custom: `You are a helpful AI assistant. Customize this prompt based on your specific use case and requirements.`
};